import React, { useEffect, useState, useContext, useMemo, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  Box,
  Checkbox,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  IconButton,
  alpha,
  useTheme,
  Typography,
} from "@mui/material";
import { AuthContext } from "../../../../contexts/AuthContext";
import PageviewIcon from "@mui/icons-material/Pageview";
import { formatAmount, getCommaOut } from "../../../../utils/hooks/dataHandler";
import PdfGenerator from "../Pdf/FaAdditionsPdf";
import ScheduleHeader from "../../../App/ScheduleHeader";
import CustomDialog from "../../../utils/dialog/customDialog";
import {
  TextFieldForName,
  TextFieldForViewOnly,
  YearsClaim,
} from "../../../utils/TextField/TextField";
import { TextFieldForAmount } from "../../../utils/TextField/NumericTextField";
import ErrorSharpIcon from "@mui/icons-material/ErrorSharp";
import ScheduleNotes from "../../ScheduleNotes";
import {
  getCaKeys,
  getCaSettingByKey,
  getCaType,
} from "../../../../builders/settings/CaSetting";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import IconButtonWithTooltip from "../../../utils/IconButtonWithTooltip";
import { useErrorDialog } from "../../../../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../../../../contexts/SuccessDialogContext";
import { JustInfo, StyledIconButton } from "../../../utils/Icon";
import AdditionExcelUpload from "./AdditionExcelUpload";
import {
  BalanceVerification,
  ExceedVerification,
  Verification,
} from "../../utils/ScheduleContent";
import ComponentLoading from "../../../App/ComponentLoading";
import FullscreenDialog from "../../../utils/dialog/FullscreenDialog";
import ValidationWarningDialog from "../../../utils/dialog/ValidationWarningDialog";
import AdditionSummary from "./AdditionSummary";
import GroupedSelect from "../../../utils/GroupedSelect";
import NoDataAvailableUI from "../../../Common/NoDataAvailable";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import Toolbar from "../../../utils/Toolbar";
import { exportComponentAsImage } from "../../../../utils/export/componentExporter";
import {
  CA_ADDITION_SOURCE_KEYS,
  CA_ADDITION_SOURCE_LABELS,
  FA_REGISTER_CURRENT_YEAR_SOURCE,
} from "../../../../utils/static/CASchedule";

const columnSettings = [
  { field: "description", align: "left", type: "text" },
  { field: "cost", align: "right", type: "numeric" },
  { field: "isDeferred", align: "center", type: "checkbox" },
  { field: "CaName", align: "center", type: "text" },
  { field: "numberOfYears", align: "center", type: "numeric" },
];

const FaAdditionsSchedule = ({
  company,
  schedule,
  scheduleDetails,
  saveSchedule,
  openScheduleWithDelay,
  deleteSchedule,
  recordSaved,
  recordUnsavedData,
  recordChangeHappened,
  canEditSchedule = true,
}) => {
  const theme = useTheme();
  const exportRef = useRef(null);
  const handleExportAsImage = async (e) => {
    e.stopPropagation();
    if (exportRef.current) {
      await exportComponentAsImage(exportRef.current, "FA Additions");
    }
  };

  const [schedules, setSchedules] = useState([]);
  const [fetchDataError, setFetchDataError] = useState(false);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);

  const { getSchedules, createPLSchedule } = useContext(AuthContext);

  const [notes, setNotes] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");

  const [loading, setLoading] = useState(true);

  const [caTypeOptions, setCaTypeOptions] = useState([]);
  const [caTypeSelectOptions, setCaTypeSelectOptions] = useState([]);
  const [filteredUsedColumns, setFilteredUsedColumns] = useState([]);

  const [assetLines, setAssetLines] = useState([]);
  const [otherSources, setOtherSources] = useState({});
  const [registeredAssetSources, setRegisteredAssetSources] = useState({});
  const [hirePurchaseSummary, setHirePurchaseSummary] = useState({});
  const [totalLines, setTotalLines] = useState({});
  const [otherSourcesTotalLines, setOtherSourcesTotalLines] = useState({});
  const [
    registeredAssetSourcesTotalLines,
    setRegisteredAssetSourcesTotalLines,
  ] = useState({});
  const [combinedTotalLines, setCombinedTotalLines] = useState({});

  const [caSettings, setCaSettings] = useState([]);
  const [refSources, setRefSources] = useState({});

  const handleError = useErrorDialog();
  const handleSuccess = useSuccessDialog();

  const [confirmCreateSchedule, setConfirmCreateSchedule] = useState(false);
  const [loadingCreateSchedule, setLoadingCreateSchedule] = useState(false);
  const [validationWarning, setValidationWarning] = useState({
    open: false,
    warnings: [],
    pendingPayload: null,
    loading: false,
  });
  const handleCreateSchedule = async () => {
    setLoadingCreateSchedule(true);

    try {
      const data = {
        tc_id: company.tc_id,
        scheduleTitle: "Renovation and Refurbishment Deductions Under S14N",
        rowType: "renovation_rows",
        scheduleType: "Renovation",
        sourceType: "ca_schedule",
        source_id: schedule.scheduleId,
        rows: [],
      };

      const response = await createPLSchedule(data);

      if (response) {
        handleSave({ skipValidationWarning: true });
      } else {
        throw new Error(
          "Failed to create Renovation Schedule. Please try again.",
        );
      }
      handleSuccess("Renovation schedule has been created successfully.");
    } catch (error) {
      handleError(error);
    } finally {
      setConfirmCreateSchedule(false);
      setLoadingCreateSchedule(false);
    }
  };

  const faAdditionsSummary = useMemo(
    () =>
      generateFaAdditionsSummary(
        assetLines,
        otherSources,
        registeredAssetSources,
      ),
    [assetLines, otherSources, registeredAssetSources],
  );

  const schedulePayload = useMemo(() => {
    return {
      notes,
      reviewNotes,
      dataRows: assetLines,
      otherSourceRows: otherSources,
      registeredAssetSourceRows: registeredAssetSources,
      faAdditionsSummary,
      hirePurchaseSummary,
      totalRenovation: combinedTotalLines.Renovation,
      totalRevenue: combinedTotalLines.Revenue,
      doc: JSON.stringify({
        assetLines,
        otherSources,
        registeredAssetSources,
        totalLines,
        otherSourcesTotalLines,
        registeredAssetSourcesTotalLines,
        combinedTotalLines,
        filteredUsedColumns,
        caSettings,
        refSources,
        notes,
        reviewNotes,
        renovationScheduleNo: schedules && schedules[0]?.scheduleNo,
      }),
    };
  }, [
    assetLines,
    otherSources,
    registeredAssetSources,
    faAdditionsSummary,
    hirePurchaseSummary,
    combinedTotalLines,
    totalLines,
    otherSourcesTotalLines,
    registeredAssetSourcesTotalLines,
    filteredUsedColumns,
    caSettings,
    refSources,
    notes,
    reviewNotes,
    schedules,
  ]);

  const markUnsaved = () => {
    recordChangeHappened();
    recordUnsavedData(schedulePayload);
  };

  // Excel upload dialog state
  const [excelDialogOpen, setExcelDialogOpen] = useState(false);
  const handleOpenExcelUploadDialog = () => setExcelDialogOpen(true);
  const handleCloseExcelUploadDialog = () => setExcelDialogOpen(false);

  const onExcelAddRows = (updatedAssetLines) => {
    const nextManualAssetLines = filterManualAssetLines(
      updatedAssetLines,
      registeredAssetSources,
    );
    let filterColumns = filterUsedColumns(
      [otherSources, registeredAssetSources, ...nextManualAssetLines],
      caTypeOptions,
    );

    setFilteredUsedColumns(filterColumns);

    let newTotal = calculateTotal(nextManualAssetLines, filterColumns);

    calculateCombinedTotal(
      newTotal,
      otherSourcesTotalLines,
      registeredAssetSourcesTotalLines,
    );

    setAssetLines(nextManualAssetLines);

    markUnsaved();
  };

  const calculateCombinedTotal = (...totalsList) => {
    const combinedTotals = {};

    const allCaNames = new Set(
      totalsList.flatMap((totals) => Object.keys(totals || {})),
    );

    allCaNames.forEach((caName) => {
      combinedTotals[caName] = totalsList.reduce(
        (sum, totals) => sum + (totals?.[caName] || 0),
        0,
      );
    });

    setCombinedTotalLines(combinedTotals);
  };

  const calculateTotal = (data, usedCaNames) => {
    const totals = {};

    usedCaNames.forEach((caName) => {
      totals[caName] = 0;
    });

    data.forEach((category) =>
      category.additionsDetails.forEach((item) => {
        if (item.CaName && usedCaNames.includes(item.CaName)) {
          totals[item.CaName] += item.cost;
        }
      }),
    );

    const totalAdditions = data.reduce(
      (sum, row) => sum + row.totalFloating,
      0,
    );

    totals["totalCost"] = totalAdditions;

    setTotalLines(totals);

    return totals;
  };

  const calculateSourceTotal = (data, usedCaNames, setTotals) => {
    const totals = {};

    usedCaNames.forEach((caName) => {
      totals[caName] = 0;
    });

    (data?.additionsDetails || []).forEach((item) => {
      if (item.CaName && usedCaNames.includes(item.CaName)) {
        totals[item.CaName] += item.cost;
      }
    });

    if (setTotals) setTotals(totals);

    return totals;
  };

  const calculateOtherSourcesTotal = (data, usedCaNames) =>
    calculateSourceTotal(data, usedCaNames, setOtherSourcesTotalLines);

  const calculateRegisteredAssetSourcesTotal = (data, usedCaNames) =>
    calculateSourceTotal(
      data,
      usedCaNames,
      setRegisteredAssetSourcesTotalLines,
    );

  const filterUsedColumns = (data, options) => {
    const caNames_set = new Set();
    data.forEach((category) => {
      (category?.additionsDetails || []).forEach((item) => {
        if (item.CaName) caNames_set.add(item.CaName);
      });
    });

    let caNames = options.filter((caName) => caNames_set.has(caName));

    return caNames.filter((caName) =>
      data.some((category) =>
        (category?.additionsDetails || []).some(
          (item) => item.CaName === caName,
        ),
      ),
    );
  };

  function generateFaAdditionsSummary(
    dataRows,
    otherSources,
    registeredAssetSources,
  ) {
    const additionsMap = {};
    const hirePurchaseAdditions = [];

    const getSourceGroup = (group, sourceKey) => {
      if (!group.sourceGroups) group.sourceGroups = {};

      if (!group.sourceGroups[sourceKey]) {
        const isFARegisterSource =
          sourceKey === CA_ADDITION_SOURCE_KEYS.faRegister;

        group.sourceGroups[sourceKey] = {
          deferred: 0,
          nonDeferred: 0,
          sourceKey,
          sourceLabel: CA_ADDITION_SOURCE_LABELS[sourceKey] || "",
          ...(isFARegisterSource
            ? {
                source: {
                  type: FA_REGISTER_CURRENT_YEAR_SOURCE,
                  source: "FA Register",
                },
                faRegisterSource: true,
                isImportedFromFARegister: true,
              }
            : {}),
        };
      }

      return group.sourceGroups[sourceKey];
    };

    const addToGroup = (group, type, cost, sourceKey) => {
      const numericCost = Number(cost) || 0;
      const sourceGroup = getSourceGroup(group, sourceKey);

      group[type] += numericCost;
      sourceGroup[type] += numericCost;
    };

    function groupAdditions(
      additions,
      sourceKey = CA_ADDITION_SOURCE_KEYS.manual,
    ) {
      additions.forEach(
        ({
          assetId,
          categoryId,
          description,
          cost,
          isDeferred,
          CaName,
          CaType,
          numberOfYears,
        }) => {
          // Default numberOfYears to 0 if undefined
          const year = numberOfYears ?? 0;

          if (
            CaName !== "" &&
            !["Renovation", "Revenue", "No Claim", "Hire Purchase"].includes(
              CaName,
            )
          ) {
            if (!additionsMap[CaName]) {
              additionsMap[CaName] = {};
            }

            if (!additionsMap[CaName][year]) {
              additionsMap[CaName][year] = {
                deferred: 0,
                nonDeferred: 0,
                caType: getCaSettingByKey(caSettings, CaName)?.caType,
                sourceGroups: {},
              };
            }

            addToGroup(
              additionsMap[CaName][year],
              isDeferred ? "deferred" : "nonDeferred",
              cost,
              sourceKey,
            );
          }

          if (CaName !== "" && ["Hire Purchase"].includes(CaName)) {
            hirePurchaseAdditions.push({
              assetName: description,
              cost: cost,
              assetId,
              categoryId,
            });
          }
        },
      );
    }

    dataRows.forEach(({ additionsDetails }) => {
      if (additionsDetails) groupAdditions(additionsDetails);
    });

    if (otherSources?.additionsDetails) {
      groupAdditions(
        otherSources.additionsDetails,
        CA_ADDITION_SOURCE_KEYS.plSchedules,
      );
    }

    if (registeredAssetSources?.additionsDetails) {
      groupAdditions(
        registeredAssetSources.additionsDetails,
        CA_ADDITION_SOURCE_KEYS.faRegister,
      );
    }

    setHirePurchaseSummary(hirePurchaseAdditions);
    return additionsMap;
  }

  const getValidationWarnings = () => {
    const warnings = [];

    assetLines.forEach((asset) => {
      const currentTotal = Math.round(
        (asset.additionsDetails || []).reduce(
          (total, detail) => total + (Number(detail.cost) || 0),
          0,
        ),
      );

      const expectedTotal = Math.round(Number(asset.cost?.additions) || 0);
      const difference = expectedTotal - currentTotal;

      if (currentTotal !== expectedTotal) {
        warnings.push({
          title: `${asset.assetCategory || "Asset category"} additions do not match`,
          description: `Current: ${formatAmount(currentTotal)}. Expected: ${formatAmount(
            expectedTotal,
          )}. Difference: ${formatAmount(difference)}.`,
        });
      }
    });

    const cappedS19AValue =
      Number(combinedTotalLines["S19A(10A) 1 year - capped"]) || 0;
    if (cappedS19AValue > 30000) {
      warnings.push({
        title: "S19A(10A) 1 year - capped exceeds the allowed cap",
        description: `Current: ${formatAmount(
          cappedS19AValue,
        )}. Cap: ${formatAmount(30000)}. Excess: ${formatAmount(
          cappedS19AValue - 30000,
        )}.`,
      });
    }

    return warnings;
  };

  const performSave = (payload = schedulePayload) => {
    saveSchedule(payload);
    recordSaved();
  };

  const handleSave = (options = {}) => {
    const warnings = options.skipValidationWarning
      ? []
      : getValidationWarnings();

    if (warnings.length > 0) {
      setValidationWarning({
        open: true,
        warnings,
        pendingPayload: schedulePayload,
        loading: false,
      });
      return false;
    }

    performSave(schedulePayload);
    return true;
  };

  const handleConfirmValidationSave = () => {
    const pendingPayload = validationWarning.pendingPayload;
    if (!pendingPayload) return;

    try {
      setValidationWarning((prev) => ({ ...prev, loading: true }));
      performSave(pendingPayload);
      setValidationWarning({
        open: false,
        warnings: [],
        pendingPayload: null,
        loading: false,
      });
    } catch (error) {
      setValidationWarning((prev) => ({ ...prev, loading: false }));
      handleError(error);
    }
  };

  const handleInputChange = (value, { index, c_index, field, field_type }) => {
    let updatedRows = [...assetLines];
    if (
      isLockedFaRegisterDetail(updatedRows[c_index]?.additionsDetails?.[index])
    ) {
      return;
    }

    if (field_type === "numeric") {
      value = Number(getCommaOut(value));
    }

    updatedRows[c_index].additionsDetails[index][field] = value;

    if (field === "CaName") {
      updatedRows[c_index].additionsDetails[index]["numberOfYears"] =
        getCaSettingByKey(
          caSettings,
          updatedRows[c_index].additionsDetails[index]["CaName"],
        )?.numberOfYears || 0;
    }

    if (field === "isDeferred") {
      const allDeferred = updatedRows[c_index].additionsDetails.every(
        (detail) => detail.isDeferred,
      );
      updatedRows[c_index].isAllDeferred = allDeferred;
    } else if (field === "cost") {
      const sumAdditionsDetails = updatedRows[c_index].additionsDetails.reduce(
        (total, detail) => total + detail.cost,
        0,
      );

      updatedRows[c_index].isCostAligned =
        sumAdditionsDetails === updatedRows[c_index].cost.additions;
      updatedRows[c_index].totalFloating = sumAdditionsDetails;
    }

    let filterColumns = filterUsedColumns(
      [otherSources, registeredAssetSources, ...updatedRows],
      caTypeOptions,
    );

    setAssetLines(updatedRows);
    setFilteredUsedColumns(filterColumns);

    let total = calculateTotal(updatedRows, filterColumns);
    calculateCombinedTotal(
      total,
      otherSourcesTotalLines,
      registeredAssetSourcesTotalLines,
    );

    markUnsaved();
  };

  const handleOtherSourceInputChange = (
    value,
    { index, field, field_type },
  ) => {
    setOtherSources((prevState) => {
      const updatedAdditionsDetails = prevState.additionsDetails.map(
        (detail, r_index) =>
          r_index === index ? { ...detail, [field]: value } : detail,
      );

      let allDeferred = prevState.isAllDeferred;

      if (field === "CaName") {
        updatedAdditionsDetails[index]["numberOfYears"] =
          getCaSettingByKey(
            caSettings,
            updatedAdditionsDetails[index]["CaName"],
          )?.numberOfYears || 0;
      }

      if (field === "isDeferred") {
        allDeferred = updatedAdditionsDetails.every(
          (detail) => detail.isDeferred,
        );
      }

      const updatedOtherSources = {
        ...prevState,
        additionsDetails: updatedAdditionsDetails,
        isAllDeferred: allDeferred,
      };

      if (field === "CaName") {
        let filterColumns = filterUsedColumns(
          [updatedOtherSources, registeredAssetSources, ...assetLines],
          caTypeOptions,
        );
        setFilteredUsedColumns(filterColumns);
        setTimeout(() => {
          let newOtherSourceTotal = calculateOtherSourcesTotal(
            updatedOtherSources,
            filterColumns,
          );
          calculateCombinedTotal(
            totalLines,
            newOtherSourceTotal,
            registeredAssetSourcesTotalLines,
          );
        }, 0);
      }

      return updatedOtherSources;
    });

    markUnsaved();
  };

  const handleAllDeferred = (value, { index }) => {
    const updatedDataRows = [...assetLines];

    updatedDataRows[index].additionsDetails.forEach((detail) => {
      if (isLockedFaRegisterDetail(detail)) return;
      detail.isDeferred = value;
    });
    updatedDataRows[index].isAllDeferred =
      updatedDataRows[index].additionsDetails.length > 0 &&
      updatedDataRows[index].additionsDetails.every(
        (detail) => detail.isDeferred === true,
      );

    setAssetLines(updatedDataRows);

    markUnsaved();
  };

  const handleAllDeferredOtherSources = (value) => {
    setOtherSources((prevState) => {
      const updatedData = {
        ...prevState,
        isAllDeferred: value,
        additionsDetails: prevState.additionsDetails.map((detail) => ({
          ...detail,
          isDeferred: value,
        })),
      };

      return updatedData;
    });

    markUnsaved();
  };

  const handleRenovationScheduleOpen = async () => {
    openScheduleWithDelay(
      {
        sch_id: schedules[0].sch_id,
        scheduleTitle: schedules[0].scheduleTitle,
        scheduleNo: schedules[0].scheduleNo,
        scheduleType: "Renovation",
      },
      "Renovation",
      false,
    );
  };

  const isFaRegisterManualValuesRow = (row = {}) =>
    row.faRegisterManualValues || row.faRegisterValueMode === "manual";

  const isLockedFaRegisterDetail = (row = {}) =>
    row.locked || row.faRegisterSourceDetail;

  const getCategorySourceIds = (row = {}) =>
    [row.assetId, row.categoryId, row.assetCategoryId, row.assetCategory]
      .filter(Boolean)
      .map((value) => String(value).trim().toLowerCase());

  const getFaRegisterDetailSourceId = (row = {}, index = 0) =>
    String(
      row.registeredAssetSourceId ||
        row.assetId ||
        row.rowKey ||
        `${row.categoryId || row.assetCategory || "fa-register"}-${index}`,
    );

  const getManualFaRegisterCategoryIds = (rows = []) => {
    const ids = new Set();
    rows.forEach((row) => {
      if (!isFaRegisterManualValuesRow(row)) return;
      getCategorySourceIds(row).forEach((id) => ids.add(id));
    });
    return ids;
  };

  const isCategoryExcludedByManualRows = (row = {}, manualRows = []) => {
    const manualIds = getManualFaRegisterCategoryIds(manualRows);
    if (manualIds.size === 0) return false;
    return getCategorySourceIds(row).some((id) => manualIds.has(id));
  };

  const filterRegisteredSourceRowsByManualRows = (
    sourceRows = {},
    manualRows = [],
  ) => {
    const manualIds = getManualFaRegisterCategoryIds(manualRows);
    if (manualIds.size === 0) return sourceRows;

    const isExcluded = (row = {}) =>
      getCategorySourceIds(row).some((id) => manualIds.has(id));

    const additionsDetails = (sourceRows.additionsDetails || []).filter(
      (detail) => !isExcluded(detail),
    );
    const categories = (sourceRows.categories || [])
      .filter((category) => !isExcluded(category))
      .map((category) => ({
        ...category,
        additionsDetails: (category.additionsDetails || []).filter(
          (detail) => !isExcluded(detail),
        ),
      }))
      .filter((category) => (category.additionsDetails || []).length > 0);
    const cost = additionsDetails.reduce(
      (total, detail) => total + (Number(detail.cost) || 0),
      0,
    );

    return {
      ...sourceRows,
      additionsDetails,
      categories,
      cost,
    };
  };

  const getRegisteredSourceDetailsForManualCategory = (
    category = {},
    sourceRows = {},
  ) => {
    const categoryIds = new Set(getCategorySourceIds(category));
    const matchesCategory = (row = {}) =>
      getCategorySourceIds(row).some((id) => categoryIds.has(id));

    const detailsFromCategories = (sourceRows.categories || [])
      .filter(matchesCategory)
      .flatMap((sourceCategory) => sourceCategory.additionsDetails || []);

    if (detailsFromCategories.length > 0) return detailsFromCategories;

    return (sourceRows.additionsDetails || []).filter(matchesCategory);
  };

  const prefillManualFaRegisterDetails = (rows = [], sourceRows = {}) =>
    rows.map((category) => {
      if (!isFaRegisterManualValuesRow(category)) return category;

      const existingDetails = category.additionsDetails || [];
      const existingSourceIds = new Set(
        existingDetails.map((detail, index) =>
          getFaRegisterDetailSourceId(detail, index),
        ),
      );
      const sourceDetails = getRegisteredSourceDetailsForManualCategory(
        category,
        sourceRows,
      );
      const missingSourceDetails = sourceDetails
        .filter((detail, index) => {
          const sourceId = getFaRegisterDetailSourceId(detail, index);
          return !existingSourceIds.has(sourceId);
        })
        .map((detail, index) => ({
          ...detail,
          assetId: getFaRegisterDetailSourceId(detail, index),
          registeredAssetSourceId: getFaRegisterDetailSourceId(detail, index),
          categoryId: category.assetId || category.categoryId,
          assetCategory: category.assetCategory,
          isImportedFromFARegister: true,
          faRegisterSource: true,
          faRegisterSourceDetail: true,
          locked: true,
        }));

      if (missingSourceDetails.length === 0) return category;

      return {
        ...category,
        additionsDetails: [...missingSourceDetails, ...existingDetails],
      };
    });

  const buildRegisteredSourceCategories = (
    sourceRows = {},
    manualRows = [],
  ) => {
    const additionsDetails = sourceRows.additionsDetails || [];
    const sourceCategories = Array.isArray(sourceRows.categories)
      ? sourceRows.categories
      : [];

    const categories =
      sourceCategories.length > 0
        ? sourceCategories
        : additionsDetails.length > 0
          ? [
              {
                assetId: "fa-register-assets",
                categoryId: "fa-register-assets",
                assetCategory: "Registered Assets",
                additionsDetails,
              },
            ]
          : [];

    return categories
      .filter(
        (category) => !isCategoryExcludedByManualRows(category, manualRows),
      )
      .map((category, index) => {
        const categoryDetails = category.additionsDetails || [];
        const cost = categoryDetails.reduce(
          (total, detail) => total + (Number(detail.cost) || 0),
          0,
        );

        return {
          ...category,
          assetId:
            category.assetId || category.categoryId || `fa-register-${index}`,
          categoryId:
            category.categoryId || category.assetId || `fa-register-${index}`,
          assetCategory: category.assetCategory || "Registered Assets",
          additionsDetails: categoryDetails,
          cost: category.cost || { additions: cost },
          totalFloating:
            category.totalFloating !== undefined
              ? category.totalFloating
              : cost,
          isCostAligned: true,
          isImportedFromFARegister: true,
          faRegisterSource: true,
          locked: true,
          isAllDeferred:
            categoryDetails.length > 0 &&
            categoryDetails.every((item) => item.isDeferred === true),
        };
      });
  };

  const normalizeSourceRows = (sourceRows = {}) => {
    const additionsDetails = sourceRows.additionsDetails || [];
    const shouldNormalizeCategories =
      sourceRows.source === "FA Register" ||
      Array.isArray(sourceRows.categories);
    const allDeferred =
      additionsDetails.length > 0 &&
      additionsDetails.every((item) => item.isDeferred === true);
    const cost = additionsDetails.reduce(
      (total, detail) => total + (Number(detail.cost) || 0),
      0,
    );

    return {
      ...sourceRows,
      additionsDetails,
      ...(shouldNormalizeCategories
        ? { categories: buildRegisteredSourceCategories(sourceRows) }
        : {}),
      isAllDeferred: allDeferred,
      cost,
    };
  };

  const isFARegisterSourceCategory = (category = {}) =>
    !isFaRegisterManualValuesRow(category) &&
    (category.source === "FA Register" ||
      category.source?.source === "FA Register" ||
      category.isImportedFromFARegister ||
      category.faRegisterSource);

  const getRegisteredCategoryIds = (sourceRows = {}) => {
    const ids = new Set();
    const addIds = (row) => {
      getCategorySourceIds(row).forEach((id) => ids.add(id));
    };

    (sourceRows.assetCategories || []).forEach(addIds);
    (sourceRows.registeredCategories || []).forEach(addIds);
    (sourceRows.categories || []).forEach(addIds);
    (sourceRows.additionsDetails || []).forEach(addIds);

    return ids;
  };

  const isMatchedFARegisterCategory = (category, registeredCategoryIds) =>
    !isFaRegisterManualValuesRow(category) &&
    (isFARegisterSourceCategory(category) ||
      getCategorySourceIds(category).some((id) =>
        registeredCategoryIds.has(id),
      ));

  const filterManualAssetLines = (
    rows = [],
    sourceRows = registeredAssetSources,
  ) => {
    const registeredCategoryIds = getRegisteredCategoryIds(sourceRows);
    return rows.filter(
      (category) =>
        !isMatchedFARegisterCategory(category, registeredCategoryIds),
    );
  };

  const getVisibleAssetLines = (
    rows = assetLines,
    sourceRows = registeredAssetSources,
  ) => {
    const registeredCategoryIds = getRegisteredCategoryIds(sourceRows);
    return rows.reduce((list, category, originalIndex) => {
      if (!isMatchedFARegisterCategory(category, registeredCategoryIds)) {
        list.push({ category, originalIndex });
      }
      return list;
    }, []);
  };

  const visibleAssetLines = useMemo(
    () => getVisibleAssetLines(assetLines, registeredAssetSources),
    [assetLines, registeredAssetSources],
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        let options = [];

        if (schedule) {
          setNotes(schedule.notes);
          setReviewNotes(schedule.reviewNotes);

          setCaSettings(schedule.caSettings);
          setRefSources(schedule.refSources);

          options = [
            ...getCaKeys(schedule.caSettings),
            "Hire Purchase",
            "Renovation",
            "Revenue",
            "No Claim",
          ];
        }

        if (scheduleDetails) {
          const nextOtherSources = normalizeSourceRows(
            scheduleDetails.otherSources,
          );
          const normalizedRegisteredAssetSources = normalizeSourceRows(
            scheduleDetails.registeredAssetSources,
          );
          let nextAssetLines = filterManualAssetLines(
            scheduleDetails.dataRows || [],
            normalizedRegisteredAssetSources,
          );
          nextAssetLines = prefillManualFaRegisterDetails(
            nextAssetLines,
            normalizedRegisteredAssetSources,
          );
          const nextRegisteredAssetSources =
            filterRegisteredSourceRowsByManualRows(
              normalizedRegisteredAssetSources,
              nextAssetLines,
            );

          if (nextRegisteredAssetSources.source === "FA Register") {
            nextRegisteredAssetSources.categories =
              buildRegisteredSourceCategories(nextRegisteredAssetSources);
            nextRegisteredAssetSources.cost =
              nextRegisteredAssetSources.additionsDetails.reduce(
                (total, detail) => total + (Number(detail.cost) || 0),
                0,
              );
          }

          if (nextAssetLines.length > 0) {
            nextAssetLines.forEach((asset) => {
              asset.additionsDetails.forEach((item) => {
                let noy = getCaSettingByKey(
                  caSettings,
                  item["CaName"],
                )?.numberOfYears;

                if (noy && noy !== null) item["numberOfYears"] = noy;
              });

              const allDeferred =
                asset.additionsDetails.length > 0 &&
                asset.additionsDetails.every(
                  (item) => item.isDeferred === true,
                );
              asset.isAllDeferred = allDeferred;

              const sumAdditionsDetails = asset.additionsDetails.reduce(
                (total, detail) => total + detail.cost,
                0,
              );

              asset.isCostAligned =
                sumAdditionsDetails === asset.cost.additions;
              asset.totalFloating = sumAdditionsDetails;
            });
          }

          setOtherSources(nextOtherSources);
          setRegisteredAssetSources(nextRegisteredAssetSources);

          let filterColumns = filterUsedColumns(
            [nextOtherSources, nextRegisteredAssetSources, ...nextAssetLines],
            options,
          );

          setAssetLines(nextAssetLines);
          setFilteredUsedColumns(filterColumns);

          let newTotal = calculateTotal(nextAssetLines, filterColumns);
          let newOtherSourceTotal = calculateOtherSourcesTotal(
            nextOtherSources,
            filterColumns,
          );
          let newRegisteredAssetSourceTotal =
            calculateRegisteredAssetSourcesTotal(
              nextRegisteredAssetSources,
              filterColumns,
            );

          calculateCombinedTotal(
            newTotal,
            newOtherSourceTotal,
            newRegisteredAssetSourceTotal,
          );
        }

        setCaTypeOptions(options);
        setCaTypeSelectOptions(
          options.map((opt) => {
            const val = getCaType(schedule.caSettings, opt);
            return {
              value: opt,
              label: opt,
              group: val,
            };
          }),
        );

        if (company.tc_id) {
          const schedules = await getSchedules(company.tc_id, "Renovation");
          if (schedules) setSchedules(schedules);
          else setSchedules(null);
        } else {
          setFetchDataError(true);
        }
      } catch (error) {
        setFetchDataError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const addNewRow = (categoryIndex) => {
    const updatedData = [...assetLines];

    const newRow = {
      assetId: uuidv4(),
      categoryId: updatedData[categoryIndex].assetId,
      description: "",
      cost: 0,
      isDeferred: false,
      CaName: "",
      numberOfYears: 0,
    };

    updatedData[categoryIndex].additionsDetails.push(newRow);
    setAssetLines(updatedData);

    markUnsaved();
  };

  const removeRow = (categoryIndex, rowIndex) => {
    if (
      isLockedFaRegisterDetail(
        assetLines[categoryIndex]?.additionsDetails?.[rowIndex],
      )
    ) {
      return;
    }

    let updatedRows = assetLines.map((row, index) => {
      if (index === categoryIndex) {
        return {
          ...row,
          additionsDetails: row.additionsDetails.filter(
            (_, i) => i !== rowIndex,
          ),
        };
      }
      return row;
    });

    updatedRows.forEach((asset) => {
      const allDeferred =
        asset.additionsDetails.length > 0 &&
        asset.additionsDetails.every((item) => item.isDeferred === true);
      asset.isAllDeferred = allDeferred;

      const sumAdditionsDetails = asset.additionsDetails.reduce(
        (total, detail) => total + detail.cost,
        0,
      );

      asset.isCostAligned = sumAdditionsDetails === asset.cost.additions;
      asset.totalFloating = sumAdditionsDetails;
    });

    let filterColumns = filterUsedColumns(
      [otherSources, registeredAssetSources, ...updatedRows],
      caTypeOptions,
    );

    setAssetLines(updatedRows);
    setFilteredUsedColumns(filterColumns);
    let newTotal = calculateTotal(updatedRows, filteredUsedColumns);

    calculateCombinedTotal(
      newTotal,
      otherSourcesTotalLines,
      registeredAssetSourcesTotalLines,
    );

    markUnsaved();
  };

  //Batch delete
  const [loadingBatchDelete, setLoadingBatchDelete] = useState(false);
  const [confirmBatchDelete, setConfirmBatchDelete] = useState(false);
  const [batchDeleteRows, setBatchDeleteRows] = useState([]);

  const handleBatchDeleteSelectAll = (checked) => {
    if (checked) {
      const allRows = [];
      assetLines.forEach((category, c_index) => {
        category.additionsDetails.forEach((row, index) => {
          if (isLockedFaRegisterDetail(row)) return;
          allRows.push({ c_index, index });
        });
      });
      setBatchDeleteRows(allRows);
    } else {
      setBatchDeleteRows([]);
    }

    markUnsaved();
  };

  const handleBatchDeleteRowSelect = (checked, { c_index, index }) => {
    if (
      isLockedFaRegisterDetail(assetLines[c_index]?.additionsDetails?.[index])
    ) {
      return;
    }

    if (checked) {
      setBatchDeleteRows((prev) => [...prev, { c_index, index }]);
    } else {
      setBatchDeleteRows((prev) =>
        prev.filter((s) => !(s.c_index === c_index && s.index === index)),
      );
    }

    markUnsaved();
  };

  const handleBatchDelete = () => {
    setLoadingBatchDelete(true);
    let updatedAssetLines = assetLines.map((line) => ({
      ...line,
      additionsDetails: [...line.additionsDetails], // Deep copy to avoid mutability issues
    }));

    // Group deletions by c_index to handle index shifting properly
    const assetRowsToDelete = batchDeleteRows
      .filter((s) => s.c_index !== undefined)
      .filter(
        ({ c_index, index }) =>
          !isLockedFaRegisterDetail(
            updatedAssetLines[c_index]?.additionsDetails?.[index],
          ),
      )
      .sort((a, b) => b.index - a.index); // Sort indices in descending order

    assetRowsToDelete.forEach(({ c_index, index }) => {
      updatedAssetLines[c_index].additionsDetails.splice(index, 1);
    });

    // Update state
    updatedAssetLines.forEach((asset) => {
      const allDeferred =
        asset.additionsDetails.length > 0 &&
        asset.additionsDetails.every((item) => item.isDeferred === true);
      asset.isAllDeferred = allDeferred;

      const sumAdditionsDetails = asset.additionsDetails.reduce(
        (total, detail) => total + detail.cost,
        0,
      );

      asset.isCostAligned = sumAdditionsDetails === asset.cost.additions;
      asset.totalFloating = sumAdditionsDetails;
    });
    let filterColumns = filterUsedColumns(
      [otherSources, registeredAssetSources, ...updatedAssetLines],
      caTypeOptions,
    );

    setAssetLines(updatedAssetLines);
    setFilteredUsedColumns(filterColumns);
    let newTotal = calculateTotal(updatedAssetLines, filteredUsedColumns);

    calculateCombinedTotal(
      newTotal,
      otherSourcesTotalLines,
      registeredAssetSourcesTotalLines,
    );
    setBatchDeleteRows([]);

    markUnsaved();
    setLoadingBatchDelete(false);
    setConfirmBatchDelete(false);
  };

  const customRenderers = {
    "S19A(10A) 1 year - capped": (value) => (
      <ExceedVerification value={value} maxAmount={30000} />
    ),

    Renovation: (value) => (
      <>
        {fetchDataError ? (
          <Verification
            isMatch={true}
            displayValue={value}
            matchTitle='Failed to fetch Renovation schedule, please refresh the page'
          />
        ) : (
          <Typography
            fontWeight={700}
            sx={{
              color: theme.palette.text.primary,
              fontSize: "0.95rem",
            }}
          >
            {Array.isArray(schedules) && schedules.length > 0 ? (
              <StyledIconButton
                icon={PageviewIcon}
                onClick={handleRenovationScheduleOpen}
                tooltip='View Renovation Schedule'
                iconColor='primary.main'
              />
            ) : (
              <Tooltip
                title='No Renovation schedule exists yet. Click here to create it.'
                arrow
                placement='top'
              >
                <IconButton
                  size='small'
                  sx={{
                    p: 0.5,
                    "&:hover": {
                      backgroundColor: "transparent",
                      transform: "scale(1.1)",
                    },
                    transition: "transform 0.2s ease-in-out",
                  }}
                  onClick={() => setConfirmCreateSchedule(true)}
                >
                  <ErrorSharpIcon
                    sx={{
                      color: theme.palette.error.main,
                      fontSize: 20,
                      filter: "drop-shadow(0 2px 4px rgba(244, 67, 54, 0.3))",
                    }}
                  />
                </IconButton>
              </Tooltip>
            )}
            {formatAmount(value || 0)}
          </Typography>
        )}
      </>
    ),
  };

  const registeredSourceCategories = buildRegisteredSourceCategories(
    registeredAssetSources,
    assetLines,
  );
  const getAssetAndRegisteredTotalLine = (caName) =>
    (Number(totalLines?.[caName]) || 0) +
    (Number(registeredAssetSourcesTotalLines?.[caName]) || 0);

  const isAllBatchDeleteSelected = () => {
    const selectableRows = [];
    assetLines.forEach((category, c_index) => {
      category.additionsDetails.forEach((row, index) => {
        if (isLockedFaRegisterDetail(row)) return;
        selectableRows.push({ c_index, index });
      });
    });

    return (
      selectableRows.length > 0 &&
      selectableRows.every((row) =>
        batchDeleteRows.some(
          (selected) =>
            selected.c_index === row.c_index && selected.index === row.index,
        ),
      )
    );
  };

  const renderMainContent = () => (
    <div style={{ width: "100%" }} ref={exportRef}>
      <Toolbar
        title={`Schedule ${schedule.scheduleNo}`}
        subtitle={schedule.type}
        onToggleFullscreen={() => setFullscreenOpen(!fullscreenOpen)}
        selectedCount={batchDeleteRows.length}
        onRemove={() => setConfirmBatchDelete(true)}
        fullscreenOpen={fullscreenOpen}
        allowSticky={true}
        size='medium'
        handleExportAsImage={handleExportAsImage}
        exportRef={exportRef}
        canEdit={canEditSchedule}
      />
      {visibleAssetLines.length > 0 ||
      (otherSources.additionsDetails &&
        otherSources.additionsDetails.length > 0) ||
      registeredSourceCategories.length > 0 ? (
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            flexDirection: "row",
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.98),
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            minHeight: 300,
          }}
        >
          <TableContainer
            component={Paper}
            style={{
              display: "block",
              maxWidth: "100%",
              maxHeight: "100%",
              overflow: "auto",
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {canEditSchedule && (
                    <TableCell
                      style={{
                        position: "sticky",
                        left: 0,
                        background: "white",
                        zIndex: 5,
                        minWidth: 50,
                      }}
                    >
                      <Checkbox
                        checked={isAllBatchDeleteSelected()}
                        onChange={(e) =>
                          handleBatchDeleteSelectAll(e.target.checked)
                        }
                        disabled={!canEditSchedule}
                      />
                    </TableCell>
                  )}
                  <TableCell
                    style={{
                      position: "sticky",
                      left: canEditSchedule ? 74 : 0,
                      background: "white",
                      zIndex: 5,
                      boxShadow: "5px 0 5px rgba(0, 0, 0, 0.03)",
                      minWidth: 250,
                    }}
                  >
                    Description
                  </TableCell>
                  <TableCell style={{ minWidth: 150 }} align='center'>
                    Cost
                  </TableCell>
                  <TableCell align='center'>Deferred</TableCell>
                  <TableCell style={{ minWidth: 170 }} align='center'>
                    Capital Allowance Type
                  </TableCell>
                  <TableCell style={{ minWidth: 120 }} align='center'>
                    No. of years <br />
                    of claim
                  </TableCell>
                  {filteredUsedColumns.map((header, index) => (
                    <TableCell
                      key={`filter_col_${index}`}
                      style={{ minWidth: 80, whiteSpace: "nowrap" }}
                      align='right'
                    >
                      {header}
                    </TableCell>
                  ))}
                  {canEditSchedule && (
                    <TableCell
                      style={{
                        position: "sticky",
                        right: 0,
                        background: "white",
                        zIndex: 2,
                        boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.05)",
                        minWidth: 100,
                      }}
                      align='center'
                    >
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleAssetLines.map(({ category, originalIndex }) => {
                  const c_index = originalIndex;

                  return (
                    <React.Fragment key={category.assetId || c_index}>
                      <TableRow>
                        <TableCell
                          colSpan={canEditSchedule ? 2 : 1}
                          style={{
                            position: "sticky",
                            left: 0,
                            background: "white",
                            zIndex: 2,
                            boxShadow: "2px 0 5px rgba(0, 0, 0, 0.05)",
                            fontWeight: "bold",
                          }}
                        >
                          &nbsp;&nbsp;&nbsp;
                          {category.assetCategory}&nbsp;
                          {canEditSchedule && (
                            <IconButtonWithTooltip
                              icon={AddCircleOutlineIcon}
                              tooltipTitle='Add Row'
                              onClick={() => addNewRow(c_index)}
                              color='primary'
                              size='small'
                            />
                          )}
                        </TableCell>
                        <TableCell align='right'>
                          <BalanceVerification
                            grossBalance={category.totalFloating}
                            expectedTotal={category.cost.additions}
                            balanceAtLeft
                          />
                        </TableCell>
                        <TableCell align='center'>
                          {canEditSchedule && (
                            <Checkbox
                              checked={category.isAllDeferred}
                              onChange={(e) =>
                                handleAllDeferred(e.target.checked, {
                                  index: c_index,
                                })
                              }
                            />
                          )}
                        </TableCell>
                        <TableCell colSpan={filteredUsedColumns.length + 2} />
                        {canEditSchedule && (
                          <TableCell
                            sx={{
                              position: "sticky",
                              right: 0,
                              background: "white",
                              zIndex: 2,
                              boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.05)",
                            }}
                            align='center'
                          >
                            <IconButtonWithTooltip
                              icon={AddCircleOutlineIcon}
                              tooltipTitle='Add Row'
                              onClick={() => addNewRow(c_index)}
                              color='primary'
                              size='small'
                            />
                          </TableCell>
                        )}
                      </TableRow>
                      {category.additionsDetails.map((row, i) => {
                        const isLockedSourceRow = isLockedFaRegisterDetail(row);

                        return (
                          <TableRow key={i}>
                            {canEditSchedule && (
                              <TableCell
                                style={{
                                  position: "sticky",
                                  left: 0,
                                  background: "white",
                                  zIndex: 2,
                                  boxShadow: "2px 0 5px rgba(0, 0, 0, 0.05)",
                                }}
                              >
                                <Checkbox
                                  checked={
                                    !isLockedSourceRow &&
                                    batchDeleteRows.some(
                                      (s) =>
                                        s.c_index === c_index && s.index === i,
                                    )
                                  }
                                  onChange={(e) =>
                                    handleBatchDeleteRowSelect(
                                      e.target.checked,
                                      {
                                        c_index,
                                        index: i,
                                      },
                                    )
                                  }
                                  disabled={isLockedSourceRow}
                                />
                              </TableCell>
                            )}
                            <TableCell
                              style={{
                                position: "sticky",
                                left: canEditSchedule ? 74 : 0,
                                background: "white",
                                zIndex: 2,
                                boxShadow: "2px 0 5px rgba(0, 0, 0, 0.05)",
                                width: 120,
                              }}
                              align={columnSettings[0].align}
                            >
                              {isLockedSourceRow ? (
                                <TextFieldForViewOnly
                                  value={row.description}
                                  sx={{ ml: canEditSchedule ? 0 : 3 }}
                                />
                              ) : (
                                <TextFieldForName
                                  value={row.description}
                                  onChange={handleInputChange}
                                  reference={{
                                    index: i,
                                    c_index: c_index,
                                    field: columnSettings[0].field,
                                    field_type: columnSettings[0].type,
                                  }}
                                  canEdit={canEditSchedule}
                                  sx={{ ml: canEditSchedule ? 0 : 3 }}
                                />
                              )}
                            </TableCell>
                            <TableCell
                              sx={{ width: 50 }}
                              align={columnSettings[1].align}
                            >
                              {isLockedSourceRow ? (
                                <TextFieldForViewOnly
                                  value={formatAmount(row.cost)}
                                />
                              ) : (
                                <TextFieldForAmount
                                  value={row.cost}
                                  onChange={handleInputChange}
                                  reference={{
                                    index: i,
                                    c_index: c_index,
                                    field: columnSettings[1].field,
                                    field_type: columnSettings[1].type,
                                  }}
                                  canEdit={canEditSchedule}
                                />
                              )}
                            </TableCell>
                            <TableCell align={columnSettings[2].align}>
                              {canEditSchedule && !isLockedSourceRow ? (
                                <Tooltip
                                  title={
                                    row.CaName === "Hire Purchase" &&
                                    "For the Hire Purchase item, the defer value can only be defined within its respective schedule."
                                  }
                                >
                                  <span>
                                    <Checkbox
                                      checked={row.isDeferred}
                                      onChange={(e) =>
                                        handleInputChange(e.target.checked, {
                                          index: i,
                                          c_index: c_index,
                                          field: columnSettings[2].field,
                                          field_type: columnSettings[2].type,
                                        })
                                      }
                                      disabled={row.CaName === "Hire Purchase"}
                                    />
                                  </span>
                                </Tooltip>
                              ) : (
                                <TextFieldForViewOnly
                                  value={row.isDeferred ? "Yes" : "No"}
                                />
                              )}
                            </TableCell>
                            <TableCell align={columnSettings[3].align}>
                              {isLockedSourceRow ? (
                                <TextFieldForViewOnly
                                  value={row.CaName || ""}
                                />
                              ) : (
                                <GroupedSelect
                                  value={row.CaName || ""}
                                  onChange={(e) =>
                                    handleInputChange(e.target.value, {
                                      index: i,
                                      c_index: c_index,
                                      field: columnSettings[3].field,
                                      field_type: columnSettings[3].type,
                                    })
                                  }
                                  options={caTypeSelectOptions}
                                  enableSearch
                                  canEdit={canEditSchedule}
                                />
                              )}
                            </TableCell>
                            <TableCell sx={{ width: 35 }} align='center'>
                              {isLockedSourceRow ? (
                                <TextFieldForViewOnly
                                  value={row?.numberOfYears || 0}
                                />
                              ) : (
                                <YearsClaim
                                  value={row?.numberOfYears || 0}
                                  handleInputChange={handleInputChange}
                                  reference={{
                                    index: i,
                                    c_index: c_index,
                                    field: columnSettings[4].field,
                                    field_type: columnSettings[4].type,
                                  }}
                                  numberOfYears={null}
                                  disabled={
                                    getCaSettingByKey(caSettings, row["CaName"])
                                      ?.numberOfYears !== null
                                  }
                                  canEdit={canEditSchedule}
                                />
                              )}
                            </TableCell>
                            {filteredUsedColumns &&
                              filteredUsedColumns.map((caName) => (
                                <TableCell key={caName} align='right'>
                                  <TextFieldForViewOnly
                                    value={
                                      row.CaName === caName
                                        ? formatAmount(row.cost)
                                        : ""
                                    }
                                  />
                                </TableCell>
                              ))}
                            {canEditSchedule && (
                              <TableCell
                                sx={{
                                  position: "sticky",
                                  right: 0,
                                  background: "white",
                                  zIndex: 2,
                                  boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.05)",
                                }}
                                align='center'
                              >
                                {!isLockedSourceRow && (
                                  <IconButtonWithTooltip
                                    icon={DeleteOutlineIcon}
                                    tooltipTitle='Delete Row'
                                    onClick={() => removeRow(c_index, i)}
                                    color='error'
                                    size='small'
                                  />
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })}

                {visibleAssetLines.length > 0 &&
                  registeredSourceCategories.length === 0 && (
                    <TableRow>
                      {canEditSchedule && <TableCell></TableCell>}
                      <TableCell></TableCell>
                      <TableCell
                        colSpan={4}
                        style={{
                          textAlign: "right",
                          fontWeight: "bold",
                        }}
                      >
                        Subtotal
                      </TableCell>
                      {filteredUsedColumns.map((caName) => (
                        <TableCell align='right' key={caName}>
                          <TextFieldForViewOnly
                            value={formatAmount(totalLines[caName]) || 0}
                          />
                        </TableCell>
                      ))}
                      {canEditSchedule && (
                        <TableCell
                          sx={{
                            position: "sticky",
                            right: 0,
                            background: "white",
                            zIndex: 2,
                            boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.05)",
                          }}
                        ></TableCell>
                      )}
                    </TableRow>
                  )}

                {registeredSourceCategories.length > 0 && (
                  <>
                    {registeredSourceCategories.map((category, c_index) => {
                      const categoryDetails = category.additionsDetails || [];
                      const categoryTotal = categoryDetails.reduce(
                        (total, detail) => total + (Number(detail.cost) || 0),
                        0,
                      );
                      const expectedTotal =
                        Number(category.cost?.additions) || categoryTotal;

                      return (
                        <React.Fragment
                          key={
                            category.categoryId || category.assetId || c_index
                          }
                        >
                          <TableRow>
                            <TableCell
                              colSpan={canEditSchedule ? 2 : 1}
                              style={{
                                position: "sticky",
                                left: 0,
                                background: "white",
                                zIndex: 2,
                                boxShadow: "2px 0 5px rgba(0, 0, 0, 0.05)",
                                fontWeight: "bold",
                              }}
                            >
                              &nbsp;&nbsp;&nbsp; {category.assetCategory}{" "}
                              <JustInfo
                                title='Imported from FA Register and auto aligned. If you want to make changes to the basic information of the asset, please update it in the FA Register. If you want to add items, please convert it in FA Summary Schedule and then come back.'
                                ml={0.75}
                                mr={0}
                              />
                            </TableCell>
                            <TableCell align='right'>
                              <BalanceVerification
                                grossBalance={categoryTotal}
                                expectedTotal={expectedTotal}
                                isMatch={true}
                              />
                            </TableCell>
                            <TableCell align='center'>
                              <TextFieldForViewOnly
                                value={category.isAllDeferred ? "Yes" : "No"}
                              />
                            </TableCell>
                            <TableCell
                              colSpan={filteredUsedColumns.length + 2}
                            />
                            {canEditSchedule && (
                              <TableCell
                                align='center'
                                sx={{
                                  position: "sticky",
                                  right: 0,
                                  background: "white",
                                  zIndex: 2,
                                  boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.05)",
                                }}
                              ></TableCell>
                            )}
                          </TableRow>

                          {categoryDetails.map((row, i) => (
                            <TableRow key={row.registeredAssetSourceId || i}>
                              <TableCell
                                colSpan={canEditSchedule ? 2 : 1}
                                style={{
                                  position: "sticky",
                                  left: 0,
                                  background: "white",
                                  zIndex: 2,
                                  boxShadow: "2px 0 5px rgba(0, 0, 0, 0.05)",
                                }}
                                align={columnSettings[0].align}
                              >
                                <TextFieldForViewOnly
                                  value={row.description}
                                  sx={{ ml: 3 }}
                                />
                              </TableCell>
                              <TableCell align={columnSettings[1].align}>
                                <TextFieldForViewOnly
                                  value={formatAmount(row.cost)}
                                />
                              </TableCell>
                              <TableCell align={columnSettings[2].align}>
                                <TextFieldForViewOnly
                                  value={row.isDeferred ? "Yes" : "No"}
                                />
                              </TableCell>
                              <TableCell align={columnSettings[3].align}>
                                <TextFieldForViewOnly
                                  value={row.CaName || ""}
                                />
                              </TableCell>
                              <TableCell align='center'>
                                <TextFieldForViewOnly
                                  value={row?.numberOfYears || 0}
                                />
                              </TableCell>
                              {filteredUsedColumns &&
                                filteredUsedColumns.map((caName) => (
                                  <TableCell key={caName} align='right'>
                                    <TextFieldForViewOnly
                                      value={
                                        row.CaName === caName
                                          ? formatAmount(row.cost)
                                          : ""
                                      }
                                    />
                                  </TableCell>
                                ))}
                              {canEditSchedule && (
                                <TableCell
                                  sx={{
                                    position: "sticky",
                                    right: 0,
                                    background: "white",
                                    zIndex: 2,
                                    boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.05)",
                                    minWidth: 100,
                                  }}
                                ></TableCell>
                              )}
                            </TableRow>
                          ))}
                        </React.Fragment>
                      );
                    })}

                    <TableRow>
                      {canEditSchedule && <TableCell></TableCell>}
                      <TableCell></TableCell>
                      <TableCell
                        colSpan={4}
                        style={{
                          textAlign: "right",
                          fontWeight: "bold",
                        }}
                      >
                        Subtotal
                      </TableCell>
                      {filteredUsedColumns.map((caName) => (
                        <TableCell align='right' key={caName}>
                          <TextFieldForViewOnly
                            value={
                              formatAmount(
                                getAssetAndRegisteredTotalLine(caName),
                              ) || 0
                            }
                          />
                        </TableCell>
                      ))}
                      {canEditSchedule && (
                        <TableCell
                          sx={{
                            position: "sticky",
                            right: 0,
                            background: "white",
                            zIndex: 2,
                            boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.05)",
                            minWidth: 100,
                          }}
                        ></TableCell>
                      )}
                    </TableRow>
                  </>
                )}

                {/* Ensure there are additionsDetails before rendering */}
                {otherSources.additionsDetails &&
                  otherSources.additionsDetails.length > 0 && (
                    <>
                      {/* Render the section header row only ONCE */}
                      <TableRow>
                        <TableCell
                          colSpan={canEditSchedule ? 2 : 1}
                          style={{
                            position: "sticky",
                            left: 0,
                            background: "white",
                            zIndex: 2,
                            boxShadow: "2px 0 5px rgba(0, 0, 0, 0.05)",
                            fontWeight: "bold",
                          }}
                        >
                          &nbsp;&nbsp;&nbsp; P&L Schedules
                        </TableCell>
                        <TableCell align='right'>
                          <TextFieldForViewOnly
                            value={formatAmount(otherSources.cost)}
                          />
                        </TableCell>
                        <TableCell align='center'>
                          {canEditSchedule && (
                            <Checkbox
                              checked={otherSources.isAllDeferred}
                              onChange={(e) =>
                                handleAllDeferredOtherSources(e.target.checked)
                              }
                            />
                          )}
                        </TableCell>
                        <TableCell colSpan={filteredUsedColumns.length + 1} />
                        <TableCell align='center'></TableCell>
                        {canEditSchedule && (
                          <TableCell
                            align='center'
                            sx={{
                              position: "sticky",
                              right: 0,
                              background: "white",
                              zIndex: 2,
                              boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.05)",
                            }}
                          ></TableCell>
                        )}
                      </TableRow>

                      {/* Render all additionsDetails rows inside this single section */}
                      {otherSources.additionsDetails.map((row, i) => (
                        <TableRow key={i}>
                          <TableCell
                            colSpan={canEditSchedule ? 2 : 1}
                            style={{
                              position: "sticky",
                              left: 0,
                              background: "white",
                              zIndex: 2,
                              boxShadow: "2px 0 5px rgba(0, 0, 0, 0.05)",
                            }}
                            align={columnSettings[0].align}
                          >
                            <TextFieldForViewOnly
                              value={`${row.description} - ${
                                row.source?.sch_no || ""
                              }`}
                              sx={{ ml: 3 }}
                            />
                          </TableCell>
                          <TableCell align={columnSettings[1].align}>
                            <TextFieldForViewOnly
                              value={formatAmount(row.cost)}
                            />
                          </TableCell>
                          <TableCell align={columnSettings[2].align}>
                            {canEditSchedule ? (
                              <Checkbox
                                checked={row.isDeferred}
                                onChange={(e) =>
                                  handleOtherSourceInputChange(
                                    e.target.checked,
                                    {
                                      index: i,
                                      field: columnSettings[2].field,
                                      field_type: columnSettings[2].type,
                                    },
                                  )
                                }
                              />
                            ) : (
                              <TextFieldForViewOnly
                                value={row.isDeferred ? "Yes" : "No"}
                              />
                            )}
                          </TableCell>
                          <TableCell align={columnSettings[3].align}>
                            <GroupedSelect
                              value={row.CaName || ""}
                              onChange={(e) =>
                                handleOtherSourceInputChange(e.target.value, {
                                  index: i,
                                  field: columnSettings[3].field,
                                  field_type: columnSettings[3].type,
                                })
                              }
                              options={caTypeSelectOptions}
                              enableSearch
                              canEdit={canEditSchedule}
                            />
                          </TableCell>
                          <TableCell>
                            <YearsClaim
                              value={row?.numberOfYears || 0}
                              handleInputChange={handleOtherSourceInputChange}
                              reference={{
                                index: i,
                                field: columnSettings[4].field,
                                field_type: columnSettings[4].type,
                              }}
                              numberOfYears={null}
                              disabled={
                                getCaSettingByKey(caSettings, row["CaName"])
                                  ?.numberOfYears !== null
                              }
                              canEdit={canEditSchedule}
                            />
                          </TableCell>
                          {filteredUsedColumns &&
                            filteredUsedColumns.map((caName) => (
                              <TableCell key={caName} align='right'>
                                <TextFieldForViewOnly
                                  value={
                                    row.CaName === caName
                                      ? formatAmount(row.cost)
                                      : ""
                                  }
                                />
                              </TableCell>
                            ))}
                          {canEditSchedule && (
                            <TableCell
                              sx={{
                                position: "sticky",
                                right: 0,
                                background: "white",
                                zIndex: 2,
                                boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.05)",
                                minWidth: 100,
                              }}
                            ></TableCell>
                          )}
                        </TableRow>
                      ))}

                      {/* Subtotal row for all otherSources.additionsDetails */}
                      <TableRow>
                        {canEditSchedule && <TableCell></TableCell>}
                        <TableCell></TableCell>
                        <TableCell
                          colSpan={4}
                          style={{
                            textAlign: "right",
                            fontWeight: "bold",
                          }}
                        >
                          Subtotal
                        </TableCell>
                        {filteredUsedColumns.map((caName) => (
                          <TableCell align='right' key={caName}>
                            <TextFieldForViewOnly
                              value={
                                formatAmount(otherSourcesTotalLines[caName]) ||
                                0
                              }
                            />
                          </TableCell>
                        ))}
                        {canEditSchedule && (
                          <TableCell
                            sx={{
                              position: "sticky",
                              right: 0,
                              background: "white",
                              zIndex: 2,
                              boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.05)",
                              minWidth: 100,
                            }}
                          ></TableCell>
                        )}
                      </TableRow>
                    </>
                  )}

                <TableRow>
                  {canEditSchedule && <TableCell></TableCell>}
                  <TableCell
                    style={{
                      position: "sticky",
                      left: 0,
                      background: "white",
                      zIndex: 5,
                      boxShadow: "5px 0 5px rgba(0, 0, 0, 0.03)",
                      minWidth: 250,
                      textAlign: "right",
                      fontWeight: "bold",
                    }}
                  >
                    Total
                  </TableCell>
                  <TableCell align='right'>
                    <TextFieldForViewOnly
                      value={formatAmount(
                        (totalLines["totalCost"] || 0) +
                          (otherSources.cost || 0) +
                          (registeredAssetSources.cost || 0),
                      )}
                    />
                  </TableCell>
                  <TableCell colSpan={3}></TableCell>
                  {filteredUsedColumns.map((caName) => (
                    <TableCell align='right' key={caName}>
                      <TextFieldForViewOnly
                        value={
                          customRenderers[caName]
                            ? customRenderers[caName](
                                combinedTotalLines[caName],
                              )
                            : formatAmount(combinedTotalLines[caName]) || 0
                        }
                      />
                    </TableCell>
                  ))}
                  {canEditSchedule && (
                    <TableCell
                      sx={{
                        position: "sticky",
                        right: 0,
                        background: "white",
                        zIndex: 2,
                        boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.05)",
                        minWidth: 100,
                      }}
                    ></TableCell>
                  )}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        <NoDataAvailableUI
          icon={AccountBalanceIcon}
          title='No Category Defined'
          content='You need to define at least one category in the FA Summary Schedule before proceeding.'
          subContent='Once a category is created, you can start filling in the addition details for each category.'
        />
      )}
    </div>
  );

  return (
    <Box sx={{ padding: 4 }}>
      {!loading ? (
        <>
          <ScheduleHeader
            {...{
              title: schedule.type,
              companyName: company.name,
              uen: company.uen,
              yearOfAssessment: company.yearOfAssessment,
              financialYearStart: company.financialYearStart,
              financialYearEnd: company.financialYearEnd,
              deleteSchedule,
              handleSave,
              handleExcel: handleOpenExcelUploadDialog,
              canUseAi: true,
              renderAiType: "fa_addition",
              canEditSchedule,
            }}
          >
            <PdfGenerator
              schedule={schedule}
              company={company}
              assetLines={assetLines}
              otherSources={otherSources}
              registeredAssetSources={registeredAssetSources}
              totalLines={totalLines}
              otherSourcesTotalLines={otherSourcesTotalLines}
              registeredAssetSourcesTotalLines={
                registeredAssetSourcesTotalLines
              }
              combinedTotalLines={combinedTotalLines}
              filteredUsedColumns={filteredUsedColumns}
              caSettings={caSettings}
              refSources={refSources}
              notes={notes}
              reviewNotes={reviewNotes}
              schedules={schedules}
            />
          </ScheduleHeader>
          <br />

          {!fullscreenOpen && renderMainContent()}

          <Box sx={{ mt: 4 }}>
            <AdditionSummary
              summaryData={faAdditionsSummary}
              yearOfAssessment={company.yearOfAssessment}
            />
          </Box>

          <Divider sx={{ marginY: 4 }} />
          <ScheduleNotes
            notes={notes}
            setNotes={setNotes}
            reviewNotes={reviewNotes}
            setReviewNotes={setReviewNotes}
            enableEdit={canEditSchedule}
          />

          <AdditionExcelUpload
            assetLines={assetLines}
            caSettings={caSettings}
            caTypeOptions={caTypeOptions}
            onExcelAddRows={onExcelAddRows}
            open={excelDialogOpen}
            handleCloseDialog={handleCloseExcelUploadDialog}
          />

          <FullscreenDialog
            open={fullscreenOpen}
            onClose={() => setFullscreenOpen(false)}
            title={schedule.type}
            subtitle={`${company.name} (${company.uen}) - YA ${company.yearOfAssessment}`}
          >
            {renderMainContent()}
          </FullscreenDialog>

          <ValidationWarningDialog
            open={validationWarning.open}
            title='Save with validation warning?'
            subtitle='Fixed asset additions checks did not match.'
            warnings={validationWarning.warnings}
            loading={validationWarning.loading}
            onClose={() =>
              setValidationWarning({
                open: false,
                warnings: [],
                pendingPayload: null,
                loading: false,
              })
            }
            onConfirm={handleConfirmValidationSave}
          />

          <CustomDialog
            open={confirmBatchDelete}
            onClose={() => setConfirmBatchDelete(false)}
            onConfirm={handleBatchDelete}
            loading={loadingBatchDelete}
            title={`Remove Selected Rows?`}
            description={`Are you sure you want to remove all selected rows?`}
            type='error'
          />

          <CustomDialog
            open={confirmCreateSchedule}
            onClose={() => setConfirmCreateSchedule(false)}
            onConfirm={handleCreateSchedule}
            loading={loadingCreateSchedule}
            title={`Renovation Schedule Creation`}
            description={`Are you sure you want to create a Renovation schedule? This action will initialize a new schedule for this item.`}
          />
        </>
      ) : (
        <ComponentLoading />
      )}
    </Box>
  );
};
export default FaAdditionsSchedule;
